import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import { useUserId } from "@/features/auth/hooks/useUserId";
import { useApiFetch } from "@/hooks/api/useApiFetch";
import { apiQueryKey } from "@/hooks/api/queryKey";

type CashOutData = {
  betId: string;
  response: {
    amount: number;
    eligible: boolean;
  };
};

const useCashOut = (entryIds: string[]) => {
  const userId = useUserId();
  const apiFetch = useApiFetch();
  const cashoutIdQueryString = queryString.stringify(
    { betIds: entryIds },
    { arrayFormat: "none" },
  );

  const {
    data,
    isFetching,
    refetch: fetchData,
  } = useQuery({
    queryKey: apiQueryKey("betting/cashout", { userId, betIds: entryIds }),
    queryFn: () =>
      apiFetch<CashOutData[]>(
        `${process.env.GATSBY_API_URI}/betting/cashout?${cashoutIdQueryString}`,
      ),
    enabled: entryIds.length > 0,
    meta: { userScoped: true },
  });

  const loading = isFetching;

  const cashoutAmountMap = useMemo(() => {
    const entryIdToAmountMap: Record<string, number> = {};

    if (!entryIds.length || !data || !Array.isArray(data))
      return entryIdToAmountMap;

    entryIds.forEach((entryId: string) => {
      const cashoutData = data.find((item) => item.betId === entryId);

      if (!cashoutData) return;

      if (!cashoutData.response.eligible) {
        entryIdToAmountMap[entryId] = 0; // cash out is not eligible
        return;
      }

      entryIdToAmountMap[entryId] = cashoutData.response.amount;
    });

    return entryIdToAmountMap;
  }, [data, entryIds, loading]);

  const getCashoutPropsByReferenceId = useCallback(
    (referenceId: string) => {
      const amount = cashoutAmountMap[referenceId];

      if (typeof amount !== "number") return;

      return {
        amount: cashoutAmountMap[referenceId],
        refresh: () => fetchData({ cancelRefetch: false }),
        loading,
      };
    },
    [cashoutAmountMap, fetchData, loading],
  );

  return {
    cashoutAmounts: cashoutAmountMap,
    fetchData,
    loading,
    getCashoutPropsByReferenceId,
  };
};

export { useCashOut };
